import { MicrosoftMark, PasswordField } from './LoginFormParts'

export type Step = 'options' | 'email' | 'newPassword' | 'forgot' | 'forgotConfirm'

export function OptionsStep({ onContinueWithEmail }: { onContinueWithEmail: () => void }) {
	return (
		<>
			<button
				className="signin-btn-auth signin-btn-auth--outline"
				type="button"
				disabled
				title="No identity provider is configured yet"
			>
				<MicrosoftMark />
				Continue with SSO
			</button>
			<div className="signin-divider">or</div>
			<button className="signin-btn-auth signin-btn-auth--solid" type="button" onClick={onContinueWithEmail}>
				Continue with email
			</button>
			<p className="signin-fine-print">
				By continuing, you acknowledge Keenfolks' <a href="#privacy">Privacy Policy</a> and agree to internal use of
				ATLAS under your workspace terms.
			</p>
		</>
	)
}

export function EmailStep({
	email,
	setEmail,
	password,
	setPassword,
	error,
	info,
	busy,
	onSubmit,
	onForgot,
	onBack,
}: {
	email: string
	setEmail: (v: string) => void
	password: string
	setPassword: (v: string) => void
	error: string | null
	info: string | null
	busy: boolean
	onSubmit: (e: React.FormEvent) => void
	onForgot: () => void
	onBack: () => void
}) {
	return (
		<form className="signin-fld-group" onSubmit={onSubmit}>
			<div className="signin-fld">
				<label htmlFor="email">Email</label>
				<input
					id="email"
					type="email"
					placeholder="you@thekeenfolks.com"
					autoComplete="email"
					value={email}
					onChange={(e) => setEmail(e.target.value)}
					required
				/>
			</div>
			<div>
				<div className="signin-fld__row">
					<label htmlFor="password">Password</label>
					<button className="signin-forgot-link" type="button" onClick={onForgot}>
						Forgot your password?
					</button>
				</div>
				<PasswordField id="password" label="" value={password} onChange={setPassword} autoComplete="current-password" />
			</div>
			{error && <p className="signin-error">{error}</p>}
			{info && <p className="signin-info">{info}</p>}
			<button className="signin-btn-auth signin-btn-auth--solid" type="submit" disabled={busy}>
				{busy ? 'Signing in…' : 'Sign in'}
			</button>
			<button className="signin-back-link" type="button" onClick={onBack}>
				← other options
			</button>
		</form>
	)
}

export function NewPasswordStep({
	newPassword,
	setNewPassword,
	error,
	busy,
	onSubmit,
}: {
	newPassword: string
	setNewPassword: (v: string) => void
	error: string | null
	busy: boolean
	onSubmit: (e: React.FormEvent) => void
}) {
	return (
		<form className="signin-fld-group" onSubmit={onSubmit}>
			<p className="signin-reset-title">Set a new password</p>
			<p className="signin-reset-lead">
				This account was just provisioned — choose a permanent password to finish signing in.
			</p>
			<PasswordField
				id="new-password"
				label="New password"
				value={newPassword}
				onChange={setNewPassword}
				autoComplete="new-password"
			/>
			{error && <p className="signin-error">{error}</p>}
			<button className="signin-btn-auth signin-btn-auth--solid" type="submit" disabled={busy}>
				{busy ? 'Saving…' : 'Set password & sign in'}
			</button>
		</form>
	)
}

export function ForgotStep({
	resetEmail,
	setResetEmail,
	error,
	busy,
	onSubmit,
	onBack,
}: {
	resetEmail: string
	setResetEmail: (v: string) => void
	error: string | null
	busy: boolean
	onSubmit: (e: React.FormEvent) => void
	onBack: () => void
}) {
	return (
		<form className="signin-fld-group" onSubmit={onSubmit}>
			<p className="signin-reset-title">Forgot your password?</p>
			<p className="signin-reset-lead">
				Enter your email address. We'll send a message with a code to reset your password.
			</p>
			<div className="signin-fld">
				<label htmlFor="reset-email">Email address</label>
				<input
					id="reset-email"
					type="email"
					placeholder="name@thekeenfolks.com"
					autoComplete="email"
					value={resetEmail}
					onChange={(e) => setResetEmail(e.target.value)}
					required
				/>
			</div>
			{error && <p className="signin-error">{error}</p>}
			<button className="signin-btn-auth signin-btn-auth--solid" type="submit" disabled={busy}>
				{busy ? 'Sending…' : 'Send reset code'}
			</button>
			<button className="signin-back-link" type="button" onClick={onBack}>
				← back
			</button>
		</form>
	)
}

export function ForgotConfirmStep({
	resetEmail,
	resetCode,
	setResetCode,
	resetNewPassword,
	setResetNewPassword,
	error,
	busy,
	onSubmit,
	onBack,
}: {
	resetEmail: string
	resetCode: string
	setResetCode: (v: string) => void
	resetNewPassword: string
	setResetNewPassword: (v: string) => void
	error: string | null
	busy: boolean
	onSubmit: (e: React.FormEvent) => void
	onBack: () => void
}) {
	return (
		<form className="signin-fld-group" onSubmit={onSubmit}>
			<p className="signin-reset-title">Check your email</p>
			<p className="signin-reset-lead">Enter the code we sent to {resetEmail}, and choose a new password.</p>
			<div className="signin-fld">
				<label htmlFor="reset-code">Code</label>
				<input
					id="reset-code"
					type="text"
					inputMode="numeric"
					placeholder="123456"
					value={resetCode}
					onChange={(e) => setResetCode(e.target.value)}
					required
				/>
			</div>
			<PasswordField
				id="reset-new-password"
				label="New password"
				value={resetNewPassword}
				onChange={setResetNewPassword}
				autoComplete="new-password"
			/>
			{error && <p className="signin-error">{error}</p>}
			<button className="signin-btn-auth signin-btn-auth--solid" type="submit" disabled={busy}>
				{busy ? 'Resetting…' : 'Reset my password'}
			</button>
			<button className="signin-back-link" type="button" onClick={onBack}>
				← back
			</button>
		</form>
	)
}
